import type {
	AuthSession,
	FetchAuthSessionOptions,
	FetchMFAPreferenceOutput,
	fetchMFAPreference,
} from "aws-amplify/auth";

export type PasskeyMFAPreference = "ENABLED" | "DISABLED";

type MFAType = NonNullable<FetchMFAPreferenceOutput["preferred"]>;

interface CreateUpdatePasskeyMFAPreferenceInput {
	endpoint: string;
	fetchAuthSession: (options?: FetchAuthSessionOptions) => Promise<AuthSession>;
	fetchMFAPreference: typeof fetchMFAPreference;
	fetchFn: typeof fetch;
}

interface MFASettings {
	Enabled: boolean;
	PreferredMfa: boolean;
}

// Amplify's updateMFAPreference does not send WebAuthnMfaSettings, so this
// calls the token-authorized SetUserMFAPreference operation directly. Cognito
// only updates the MFA settings present in the request body. However, it
// rejects enabling passkey MFA unless the same request also enables another
// MFA setting, even when the user already has one enabled. So the user's
// currently enabled settings are resent with their preference unchanged.
export function createUpdatePasskeyMFAPreference({
	endpoint,
	fetchAuthSession,
	fetchMFAPreference,
	fetchFn,
}: CreateUpdatePasskeyMFAPreferenceInput) {
	return async function updatePasskeyMFAPreference(
		preference: PasskeyMFAPreference,
	): Promise<void> {
		const { tokens } = await fetchAuthSession();
		if (!tokens) {
			throw new Error("No access token");
		}
		const mfaPreference = await fetchMFAPreference();
		const response = await fetchFn(endpoint, {
			method: "POST",
			headers: {
				"Content-Type": "application/x-amz-json-1.1",
				"X-Amz-Target":
					"AWSCognitoIdentityProviderService.SetUserMFAPreference",
			},
			body: JSON.stringify({
				AccessToken: tokens.accessToken.toString(),
				SMSMfaSettings: enabledMFASettings(mfaPreference, "SMS"),
				SoftwareTokenMfaSettings: enabledMFASettings(mfaPreference, "TOTP"),
				EmailMfaSettings: enabledMFASettings(mfaPreference, "EMAIL"),
				WebAuthnMfaSettings: { Enabled: preference === "ENABLED" },
			}),
		});
		if (!response.ok) {
			throw new Error(await readCognitoErrorMessage(response));
		}
	};
}

// Undefined drops the setting from the JSON body, so Cognito leaves it as is.
function enabledMFASettings(
	{ enabled, preferred }: FetchMFAPreferenceOutput,
	mfaType: MFAType,
): MFASettings | undefined {
	if (!enabled?.includes(mfaType)) {
		return undefined;
	}
	return { Enabled: true, PreferredMfa: preferred === mfaType };
}

async function readCognitoErrorMessage(response: Response): Promise<string> {
	const body: { __type?: string; message?: string } = await response
		.json()
		.catch(() => ({}));
	const type = body.__type ?? `HTTP ${response.status}`;
	return body.message ? `${type}: ${body.message}` : type;
}
